import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Calendar as CalendarIcon, 
  Check, 
  AlertCircle, 
  Twitter, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Youtube,
  Clock,
  Sparkles,
  Info,
  CheckCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { addMonths, format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isWeekend } from 'date-fns';

interface AutoFillModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  currentMonth: Date;
}

interface PlatformSchedule {
  id: string;
  name: string;
  icon: React.ElementType;
  color: string;
  selected: boolean;
  frequency: 'daily' | 'weekdays' | 'weekends' | 'custom' | '3x-week';
  customDays?: string[];
  bestTimes?: string[];
}

const AutoFillModal: React.FC<AutoFillModalProps> = ({ 
  isOpen, 
  onClose,
  onSuccess,
  currentMonth
}) => {
  const [step, setStep] = useState(1);
  const [platforms, setPlatforms] = useState<PlatformSchedule[]>([
    { 
      id: 'twitter', 
      name: 'X (Twitter)', 
      icon: Twitter, 
      color: '#1DA1F2', 
      selected: false,
      frequency: 'weekdays',
      bestTimes: ['9:00', '11:00', '14:00']
    },
    { 
      id: 'instagram', 
      name: 'Instagram', 
      icon: Instagram, 
      color: '#E4405F', 
      selected: false,
      frequency: '3x-week',
      bestTimes: ['9:00', '12:00', '18:00']
    },
    { 
      id: 'facebook', 
      name: 'Facebook', 
      icon: Facebook, 
      color: '#1877F2', 
      selected: false,
      frequency: '3x-week',
      bestTimes: ['8:00', '10:00', '13:00', '15:00']
    },
    { 
      id: 'linkedin', 
      name: 'LinkedIn', 
      icon: Linkedin, 
      color: '#0A66C2', 
      selected: false,
      frequency: 'weekdays',
      bestTimes: ['7:00', '9:00', '16:00', '18:00']
    },
    { 
      id: 'youtube', 
      name: 'YouTube', 
      icon: Youtube, 
      color: '#FF0000', 
      selected: false,
      frequency: 'custom',
      customDays: ['thursday', 'friday'],
      bestTimes: ['12:00', '15:00']
    }
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [previewEvents, setPreviewEvents] = useState<Array<{
    title: string;
    platform: string;
    date: Date;
    time: string;
  }>>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setError(null);
      setSuccess(false);
      loadUserPreferences();
    }
  }, [isOpen]);

  // Load user's existing preferences
  const loadUserPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('posting_schedules')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        // Update platforms with saved preferences
        setPlatforms(prev => 
          prev.map(platform => ({
            ...platform,
            selected: data.platforms.includes(platform.id),
            frequency: data.schedule_config[platform.id]?.frequency || platform.frequency,
            customDays: data.schedule_config[platform.id]?.customDays || platform.customDays,
            bestTimes: data.schedule_config[platform.id]?.bestTimes || platform.bestTimes
          }))
        );
      }
    } catch (err) {
      console.error('Error loading posting schedule:', err);
    }
  };

  // Toggle platform selection
  const togglePlatform = (id: string) => {
    setPlatforms(prev => 
      prev.map(platform => 
        platform.id === id 
          ? { ...platform, selected: !platform.selected }
          : platform
      )
    );
  };

  // Update platform frequency
  const updateFrequency = (id: string, frequency: 'daily' | 'weekdays' | 'weekends' | 'custom' | '3x-week') => {
    setPlatforms(prev => 
      prev.map(platform => 
        platform.id === id 
          ? { ...platform, frequency }
          : platform
      )
    );
  };

  // Generate preview events based on selected platforms and frequencies
  const generatePreview = () => {
    const events: Array<{
      title: string;
      platform: string;
      date: Date;
      time: string;
    }> = [];

    const selectedPlatforms = platforms.filter(p => p.selected);
    
    if (selectedPlatforms.length === 0) {
      setError('Please select at least one platform');
      return;
    }

    setError(null);
    
    // Get all days in the current month
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Generate events for each selected platform
    selectedPlatforms.forEach(platform => {
      const platformTimes = platform.bestTimes || ['12:00'];
      
      daysInMonth.forEach(day => {
        const dayOfWeek = format(day, 'EEEE').toLowerCase();
        
        // Check if we should create an event for this day based on frequency
        let shouldCreateEvent = false;
        
        switch (platform.frequency) {
          case 'daily':
            shouldCreateEvent = true;
            break;
          case 'weekdays':
            shouldCreateEvent = !isWeekend(day);
            break;
          case 'weekends':
            shouldCreateEvent = isWeekend(day);
            break;
          case 'custom':
            shouldCreateEvent = platform.customDays?.includes(dayOfWeek) || false;
            break;
          case '3x-week':
            // Monday, Wednesday, Friday
            shouldCreateEvent = ['monday', 'wednesday', 'friday'].includes(dayOfWeek);
            break;
        }
        
        if (shouldCreateEvent) {
          // Pick a random time from the best times
          const randomTimeIndex = Math.floor(Math.random() * platformTimes.length);
          const time = platformTimes[randomTimeIndex];
          
          events.push({
            title: `Post on ${platform.name}`,
            platform: platform.id,
            date: day,
            time
          });
        }
      });
    });
    
    setPreviewEvents(events);
    setStep(2);
  };

  // Save the schedule to the database
  const saveSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');
      
      // Create schedule config object
      const scheduleConfig: Record<string, any> = {};
      platforms.forEach(platform => {
        if (platform.selected) {
          scheduleConfig[platform.id] = {
            frequency: platform.frequency,
            customDays: platform.customDays,
            bestTimes: platform.bestTimes
          };
        }
      });
      
      // Save to database
      const { error: upsertError } = await supabase
        .from('posting_schedules')
        .upsert({
          user_id: user.id,
          platforms: platforms.filter(p => p.selected).map(p => p.id),
          schedule_config: scheduleConfig
        }, {
          onConflict: 'user_id'
        });
        
      if (upsertError) throw upsertError;
      
      // Create calendar events
      const eventPromises = previewEvents.map(event => {
        const eventDate = new Date(event.date);
        const [hours, minutes] = event.time.split(':').map(Number);
        eventDate.setHours(hours, minutes);
        
        return supabase
          .from('calendar_events')
          .insert({
            user_id: user.id,
            title: event.title,
            start_date: eventDate.toISOString(),
            tags: [event.platform, 'content', 'auto-generated'],
            color: platforms.find(p => p.id === event.platform)?.color || '#4f46e5',
            event_type: 'social_media'
          });
      });
      
      await Promise.all(eventPromises);
      
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
      
    } catch (err) {
      console.error('Error saving schedule:', err);
      setError(err instanceof Error ? err.message : 'Failed to save schedule');
    } finally {
      setLoading(false);
    }
  };

  // Get platform icon component
  const getPlatformIcon = (platform: PlatformSchedule) => {
    const Icon = platform.icon;
    return <Icon style={{ color: platform.color }} />;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                      <Sparkles className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      AI Content Planner
                    </h2>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                {/* Step indicator */}
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        step >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        1
                      </div>
                      <span className={`text-sm font-medium ${
                        step >= 1 ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        Select Platforms
                      </span>
                    </div>
                    <div className="flex-1 h-0.5 mx-4 bg-gray-200 dark:bg-dark-700"></div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        step >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-200 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        2
                      </div>
                      <span className={`text-sm font-medium ${
                        step >= 2 ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        Review & Confirm
                      </span>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
                    <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
                  </div>
                )}

                {success && (
                  <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400" />
                    <p className="text-sm text-green-600 dark:text-green-300">
                      Calendar successfully updated with your posting schedule!
                    </p>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-6">
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4 flex items-start space-x-3">
                      <Info className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-indigo-900 dark:text-indigo-100">
                          Select the platforms you want to create a posting schedule for. The wizard will automatically generate a calendar of posts based on best practices for each platform.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {platforms.map(platform => (
                        <div 
                          key={platform.id}
                          className={`p-4 rounded-lg border-2 transition-all ${
                            platform.selected 
                              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
                              : 'border-gray-200 dark:border-dark-700'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 rounded-lg bg-white dark:bg-dark-700">
                                {getPlatformIcon(platform)}
                              </div>
                              <span className="font-medium text-gray-900 dark:text-white">
                                {platform.name}
                              </span>
                            </div>
                            <button
                              onClick={() => togglePlatform(platform.id)}
                              className={`px-3 py-1 rounded-lg text-sm font-medium ${
                                platform.selected
                                  ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
                                  : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400'
                              }`}
                            >
                              {platform.selected ? 'Selected' : 'Select'}
                            </button>
                          </div>

                          {platform.selected && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700"
                            >
                              <div className="space-y-4">
                                <div className="space-y-2">
                                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Posting Frequency
                                  </label>
                                  <div className="flex flex-wrap gap-2">
                                    {[
                                      { id: 'daily', label: 'Daily' },
                                      { id: 'weekdays', label: 'Weekdays' },
                                      { id: 'weekends', label: 'Weekends' },
                                      { id: '3x-week', label: '3x Week' }
                                    ].map(freq => (
                                      <button
                                        key={freq.id}
                                        type="button"
                                        onClick={() => updateFrequency(platform.id, freq.id as any)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                          platform.frequency === freq.id
                                            ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-2 border-indigo-500'
                                            : 'bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 border-2 border-transparent'
                                        }`}
                                      >
                                        {freq.label}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                  <Clock className="h-4 w-4 text-gray-400" />
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Best times: {platform.bestTimes?.join(', ')}
                                  </span>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-6">
                    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-indigo-900 dark:text-indigo-100 mb-2">
                        Preview for {format(currentMonth, 'MMMM yyyy')}
                      </h3>
                      <p className="text-sm text-indigo-700 dark:text-indigo-300 mb-4">
                        We've generated {previewEvents.length} scheduled posting slots across {platforms.filter(p => p.selected).length} platforms.
                      </p>

                      <div className="space-y-4">
                        {platforms
                          .filter(p => p.selected)
                          .map(platform => {
                            const platformEvents = previewEvents.filter(e => e.platform === platform.id);
                            return (
                              <div key={platform.id} className="flex items-center space-x-3">
                                <div className="p-1.5 rounded-full" style={{ backgroundColor: `${platform.color}20` }}>
                                  {getPlatformIcon(platform)}
                                </div>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {platform.name}: {platformEvents.length} posts
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    <div className="max-h-60 overflow-y-auto border border-gray-200 dark:border-dark-700 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
                        <thead className="bg-gray-50 dark:bg-dark-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Platform
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                              Time
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-700">
                          {previewEvents
                            .sort((a, b) => a.date.getTime() - b.date.getTime())
                            .slice(0, 10) // Show only first 10 events in preview
                            .map((event, index) => {
                              const platform = platforms.find(p => p.id === event.platform);
                              return (
                                <tr key={index}>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0 h-5 w-5">
                                        {platform && getPlatformIcon(platform)}
                                      </div>
                                      <div className="ml-3">
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                          {platform?.name}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900 dark:text-white">
                                      {format(event.date, 'EEEE, MMMM d')}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900 dark:text-white">
                                      {event.time}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                      {previewEvents.length > 10 && (
                        <div className="px-6 py-3 bg-gray-50 dark:bg-dark-700 text-center text-sm text-gray-500 dark:text-gray-400">
                          + {previewEvents.length - 10} more events
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="mt-8 flex justify-end space-x-3">
                  {step === 1 ? (
                    <>
                      <button
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={generatePreview}
                        disabled={!platforms.some(p => p.selected)}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${
                          platforms.some(p => p.selected)
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                            : 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        Next
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setStep(1)}
                        className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-700"
                      >
                        Back
                      </button>
                      <button
                        onClick={saveSchedule}
                        disabled={loading}
                        className={`px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 ${
                          loading ? 'opacity-70 cursor-not-allowed' : ''
                        }`}
                      >
                        {loading ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Saving...
                          </span>
                        ) : (
                          'Confirm & Create Events'
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AutoFillModal;